import { useState } from "react";
import { useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { user, login, checking } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!checking && user) {
    const next = new URLSearchParams(location.search).get("next");
    const OS_ROLES = ["admin", "manager", "agent", "telecaller", "tele_caller", "builder"];
    const to = OS_ROLES.includes(user.role) ? "/os" : (next || location.state?.from || "/");
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      const role = res.user?.role;
      const next = new URLSearchParams(location.search).get("next");
      const OS_ROLES = ["admin", "manager", "agent", "telecaller", "tele_caller", "builder"];
      const dest = OS_ROLES.includes(role) ? "/os" : (next || location.state?.from || "/");
      nav(dest, { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:flex flex-col justify-between bg-forest text-white p-10 relative overflow-hidden">
        <img src="/brand/ofr-lockup.png" alt="" aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.14] pointer-events-none select-none" />
        <div className="absolute inset-0 bg-gradient-to-br from-forest/85 via-forest/70 to-forest/50 pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-3">
          <img src="/brand/ofr-mark.png" alt="OFR" className="w-11 h-11 rounded-md object-cover" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-lg">Our First Realty</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold mt-0.5">Your first step to the right property</div>
          </div>
        </Link>
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] text-gold mb-3">Sign in</div>
          <h1 className="font-heading font-extrabold text-4xl leading-tight tracking-tight max-w-md">
            Save projects. Track your inquiries. Get first look at new launches.
          </h1>
        </div>
        <div className="relative text-xs text-white/50">© {new Date().getFullYear()} Our First Realty LLP</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-gold/15 text-forest px-3 py-1 text-xs mb-6">
            <KeyRound className="w-3.5 h-3.5 text-gold" /> Sign in
          </div>
          <h2 className="font-heading font-extrabold text-3xl text-forest tracking-tight">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Buyers, builders and team — sign in below.</p>

          <GoogleSignInButton label="Continue with Google" />
          <div className="flex items-center gap-3 my-5 text-[11px] uppercase tracking-widest text-muted-foreground">
            <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="l-email" className="text-xs">Email</Label>
              <Input id="l-email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="l-pw" className="text-xs">Password</Label>
                <Link to="/forgot-password" className="text-[11px] text-muted-foreground hover:text-forest">Forgot?</Link>
              </div>
              <Input id="l-pw" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-forest hover:bg-forest-hover text-white rounded-full">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            New here? <Link to="/signup" className="text-forest font-medium hover:text-gold-hover">Create a free buyer account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
